/**
 * ALL TASKS DONE CELEBRATION — animazione mostrata quando l'utente ha
 * terminato tutti i task (lista globalmente vuota).
 *
 * - `justCompleted` true  -> l'animazione parte una volta (loop disattivato)
 *   e si ferma da sola sull'ultimo frame.
 * - `justCompleted` false -> nessun replay (es. si arriva su una pagina gia'
 *   vuota): si salta direttamente all'ultimo frame.
 *
 * Il playback e' pilotato in modo imperativo (non via prop `autoplay`)
 * perche' `justCompleted` puo' passare a true SUBITO DOPO il mount: l'effetto
 * reagisce al cambio del flag e al caricamento dell'animazione, evitando il
 * caso in cui Lottie venga inizializzato con autoplay=false e non parta piu'.
 *
 * L'animazione vive in `assets/animations/feelhippo_task.json` ed e'
 * sostituibile con l'export Lottie definitivo senza toccare questo file.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import feelhippoTaskAnimation from '../assets/animations/feelhippo_task.json';

interface AllTasksDoneCelebrationProps {
  justCompleted: boolean;
}

export function AllTasksDoneCelebration({ justCompleted }: AllTasksDoneCelebrationProps) {
  const { t } = useTranslation();
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  // L'animazione lottie-web si carica in modo asincrono: applichiamo lo stato
  // solo dopo `onDOMLoaded`, quando i metodi del ref sono operativi.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const lottie = lottieRef.current;
    if (!loaded || !lottie) return;
    if (justCompleted) {
      lottie.goToAndPlay(0, true);
    } else {
      // Nessun completamento "live": mostriamo l'ultimo frame senza replay.
      const totalFrames = lottie.getDuration(true) ?? 0;
      lottie.goToAndStop(Math.max(0, totalFrames - 1), true);
    }
  }, [justCompleted, loaded]);

  return (
    <div className="all-tasks-done">
      <Lottie
        lottieRef={lottieRef}
        animationData={feelhippoTaskAnimation}
        loop={false}
        autoplay={false}
        onDOMLoaded={() => setLoaded(true)}
        className="all-tasks-done__animation"
      />
      <p className="text-lg font-semibold">{t('task.celebration.title')}</p>
      <p>{t('task.celebration.subtitle')}</p>
    </div>
  );
}
